import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import cpApi from "@/lib/clientPortalApi";
import { PageHeader, inr } from "./_cpShared";
import { useCpAuth } from "@/contexts/ClientPortalAuthContext";
import SectionDataTable from "./SectionDataTable";
import { SECTION_META } from "./_sectionMeta";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText } from "lucide-react";

export default function CpSection() {
  const { section } = useParams();
  const { user } = useCpAuth();
  const [searchParams] = useSearchParams();
  const meta = SECTION_META[section];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pre-applied filter via URL (?filter_col=...&filter_val=...) — used by
  // dashboard drill-down clicks on Top HSN / Top Customers charts.
  const initialFilters = (() => {
    const col = searchParams.get("filter_col");
    const val = searchParams.get("filter_val");
    if (!col || !val) return null;
    return { [col]: { kind: "text", op: "equals", q: val } };
  })();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await cpApi.get(`/client-portal/data/${section}`);
      setRows(r.data || []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await cpApi.get(`/client-portal/data/${section}`);
        if (active) setRows(r.data || []);
      } catch (err) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [section]);

  if (!meta) {
    return (
      <div>
        <PageHeader title="Section not found" />
        <div className="p-8 text-sm text-slate-500">Unknown section: <span className="font-mono">{section}</span></div>
      </div>
    );
  }

  const canMutate = user?.role === "company_admin";
  const totalAmount = meta.columns.some((c) => c.key === "total_taxable_value")
    ? rows.reduce((s, r) => s + (Number(r.total_taxable_value) || 0), 0)
    : null;
  const lastUpdated = rows.reduce((acc, r) => {
    const t = r.uploaded_at || "";
    return t > acc ? t : acc;
  }, "");

  const exportSection = async (format) => {
    try {
      const r = await cpApi.get(
        `/client-portal/data/${section}/export?format=${format}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m ? m[1] : `${section}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <PageHeader
        subtitle="Client Portal"
        title={meta.label}
        action={
          <div className="flex items-center gap-3">
            {totalAmount != null && (
              <div className="text-right">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Total Taxable</div>
                <div className="text-lg font-bold text-[#0a2540]" style={{ fontFamily: "Outfit" }}>{inr(totalAmount)}</div>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="cp-export-btn">
                  <Download size={14} className="mr-2" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportSection("xlsx")} data-testid="cp-export-xlsx">
                  <FileSpreadsheet size={14} className="mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportSection("pdf")} data-testid="cp-export-pdf">
                  <FileText size={14} className="mr-2" /> PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      <div className="p-4 sm:p-8">
        <SectionDataTable
          key={section}
          columns={meta.columns}
          rows={rows}
          loading={loading}
          onReload={reload}
          apiBase={`/client-portal/data/${section}`}
          canMutate={canMutate}
          allowDelete={false}
          lastUpdated={lastUpdated}
          initialFilters={initialFilters}
        />
      </div>
    </div>
  );
}
